import { useState, useMemo, useEffect } from "react";
import { ArrowLeft, Building2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BottomNav } from "./bottom-nav";
import { formatCurrency } from "@/config/locale";
import type { BankAccount } from "./payment-methods/bank-account-item";

interface WithdrawPageProps {
  onBack: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

type WithdrawStep = "amount" | "confirm" | "success";

export function WithdrawPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: WithdrawPageProps) {
  const handleManagePayoutDetails = () => {
    toast.info(
      "Contas para recebimento são gerenciadas pela Stripe (Conta Digital). Esta tela será integrada em breve."
    );
  };

  const [step, setStep] = useState<WithdrawStep>("amount");
  const [amount, setAmount] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  /** Until wired to Conta Digital / Stripe balance API */
  const availableBalance = 0;
  const bankAccounts: BankAccount[] = [];

  const handleNavigateToPaymentMethods = () => {
    handleManagePayoutDetails();
  };

  useEffect(() => {
    if (bankAccounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const def = bankAccounts.find((a) => a.isDefault) || bankAccounts[0];
    setSelectedAccountId((prev) => (prev && bankAccounts.some((a) => a.id === prev) ? prev : def.id));
  }, [bankAccounts]);

  const selectedAccount = useMemo(
    () => bankAccounts.find((acc) => acc.id === selectedAccountId),
    [bankAccounts, selectedAccountId]
  );

  // Parse amount to number
  const amountValue = useMemo(() => {
    const numericValue = amount.replace(/[^\d]/g, "");
    return numericValue ? parseFloat(numericValue) / 100 : 0;
  }, [amount]);

  // Format amount for display
  const formattedAmount = useMemo(() => {
    if (!amount) return "R$ 0,00";
    return formatCurrency(amountValue);
  }, [amount, amountValue]);

  // Validation
  const isValidAmount = useMemo(
    () => amountValue > 0 && amountValue <= availableBalance,
    [amountValue, availableBalance]
  );

  const hasMinimumAmount = useMemo(() => amountValue >= 10, [amountValue]);

  const handleAmountChange = (value: string) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^\d]/g, "");
    setAmount(numericValue);
  };

  const handleQuickAmount = (value: number) => {
    const cents = Math.floor(value * 100);
    setAmount(cents.toString());
  };

  const handleContinue = () => {
    if (!isValidAmount || !hasMinimumAmount) return;
    setStep("confirm");
  };

  const handleConfirm = () => {
    // In a real app, this would call an API to process the withdrawal
    setStep("success");
  };

  const handleBackToWallet = () => {
    onBack();
  };

  const handleNewWithdraw = () => {
    setStep("amount");
    setAmount("");
  };

  // Step 3: Success
  if (step === "success") {
    return (
      <div className="min-h-screen bg-background pb-24 flex flex-col">
        {/* Header */}
        <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 px-4 py-4">
              <h1 className="text-lg font-medium text-white">Saque Solicitado</h1>
            </div>
          </div>
        </header>

        {/* Success Content */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-3">Saque Solicitado!</h2>
            <p className="text-muted-foreground mb-8">
              Seu saque de <strong>{formattedAmount}</strong> foi solicitado com sucesso.
              O valor será transferido para sua conta em até 1 dia útil.
            </p>

            {/* Account Details */}
            {selectedAccount && (
              <div className="bg-card border border-border rounded-xl p-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{selectedAccount.bankName}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedAccount.accountType === "checking" ? "Conta Corrente" : "Conta Poupança"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ag {selectedAccount.agency} • CC {selectedAccount.accountNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
              <p className="text-sm text-blue-900">
                <strong>Importante:</strong> Acompanhe o status do seu saque na seção de transações da Conta Digital.
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={handleBackToWallet}
                className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                Voltar para Conta Digital
              </button>
              <button
                onClick={handleNewWithdraw}
                className="w-full py-3 bg-card border border-border text-foreground rounded-xl font-medium hover:bg-accent transition-colors"
              >
                Fazer Novo Saque
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Navigation */}
        <BottomNav
          activeTab={activeTab}
          onTabChange={onTabChange}
          favoritesCount={favoritesCount}
          onMenuClick={onMenuClick}
          onPostAdClick={onPostAdClick}
          postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
        />
      </div>
    );
  }

  // Step 2: Confirmation
  if (step === "confirm") {
    return (
      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 px-4 py-4">
              <button
                onClick={() => setStep("amount")}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
                aria-label="Voltar"
              >
                <ArrowLeft className="w-6 h-6 text-white" />
              </button>
              <h1 className="text-lg font-medium text-white">Confirmar Saque</h1>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Amount Summary */}
          <div className="bg-card border border-border rounded-xl p-6 mb-6">
            <p className="text-sm text-muted-foreground mb-2">Valor do saque</p>
            <p className="text-3xl font-semibold text-primary mb-4">{formattedAmount}</p>
            
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Taxa de transferência</span>
                <span className="font-medium text-green-600">Grátis</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Você receberá</span>
                <span className="font-semibold text-lg">{formattedAmount}</span>
              </div>
            </div>
          </div>

          {/* Destination Account */}
          <div className="mb-6">
            <h2 className="font-medium mb-4">Conta de destino</h2>
            {selectedAccount ? (
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-medium">{selectedAccount.bankName}</p>
                      {selectedAccount.isDefault && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Principal
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {selectedAccount.accountType === "checking" ? "Conta Corrente" : "Conta Poupança"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ag {selectedAccount.agency} • CC {selectedAccount.accountNumber}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedAccount.accountHolderName}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <p className="text-muted-foreground mb-4">Nenhuma conta selecionada</p>
                <button
                  onClick={handleNavigateToPaymentMethods}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
                >
                  Adicionar Conta
                </button>
              </div>
            )}
          </div>

          {/* Timeline Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Prazo de transferência
            </h3>
            <p className="text-sm text-blue-800">
              O valor será transferido para sua conta em até <strong>1 dia útil</strong>.
              Saques solicitados após 17h ou em finais de semana serão processados no próximo dia útil.
            </p>
          </div>

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={!selectedAccount}
            className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmar Saque
          </button>
        </div>

        {/* Bottom Navigation */}
        <BottomNav
          activeTab={activeTab}
          onTabChange={onTabChange}
          favoritesCount={favoritesCount}
          onMenuClick={onMenuClick}
          onPostAdClick={onPostAdClick}
          postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
        />
      </div>
    );
  }

  // Step 1: Amount Entry
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Sacar Dinheiro</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Available Balance */}
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-6 mb-6 text-white">
          <p className="text-sm opacity-90 mb-2">Saldo disponível para saque</p>
          <p className="text-3xl font-semibold">{formatCurrency(availableBalance)}</p>
          <p className="text-xs opacity-90 mt-3 leading-relaxed">
            O saldo real aparece na <strong>Conta Digital</strong> (Stripe). Esta tela será sincronizada com o saldo
            em breve; saques seguem as regras da sua conta conectada.
          </p>
        </div>

        {/* Amount Input */}
        <div className="mb-6">
          <label className="block font-medium mb-3">Quanto você deseja sacar?</label>
          <div className="relative">
            <input
              type="text"
              value={formattedAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              className="w-full text-4xl font-semibold text-primary bg-transparent border-b-2 border-border focus:border-primary outline-none pb-3 transition-colors"
              placeholder="R$ 0,00"
            />
          </div>
          
          {/* Validation Messages */}
          <div className="mt-3 space-y-2">
            {amountValue > 0 && !hasMinimumAmount && (
              <p className="text-sm text-orange-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Valor mínimo para saque: R$ 10,00
              </p>
            )}
            {amountValue > availableBalance && (
              <p className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Saldo insuficiente
              </p>
            )}
          </div>
        </div>

        {/* Quick Amount Buttons */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-3">Valores rápidos</p>
          <div className="grid grid-cols-4 gap-2">
            {[50, 100, 200, 500].map((value) => (
              <button
                key={value}
                onClick={() => handleQuickAmount(value)}
                disabled={value > availableBalance}
                className="py-2.5 bg-card border border-border rounded-lg font-medium hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                R$ {value}
              </button>
            ))}
          </div>
          <button
            onClick={() => handleQuickAmount(availableBalance)}
            disabled={availableBalance === 0}
            className="w-full mt-2 py-2.5 bg-card border border-border rounded-lg font-medium hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Sacar tudo
          </button>
        </div>

        {/* Destination Account Preview */}
        {bankAccounts.length > 0 ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-medium">Conta de destino</h2>
              {bankAccounts.length > 1 && (
                <button
                  type="button"
                  onClick={handleManagePayoutDetails}
                  className="text-sm text-primary hover:underline"
                >
                  Trocar conta
                </button>
              )}
            </div>
            {selectedAccount && (
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-medium">{selectedAccount.bankName}</p>
                      {selectedAccount.isDefault && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Principal
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {selectedAccount.accountType === "checking" ? "Conta Corrente" : "Conta Poupança"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Ag {selectedAccount.agency} • CC {selectedAccount.accountNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
            <p className="text-sm text-orange-900 mb-3">
              <strong>Atenção:</strong> Você precisa cadastrar uma conta bancária para receber o saque.
            </p>
            <button
              type="button"
              onClick={handleManagePayoutDetails}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Adicionar Conta Bancária
            </button>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">Informações importantes</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Saques são <strong>gratuitos</strong></li>
            <li>• Prazo de transferência: <strong>até 1 dia útil</strong></li>
            <li>• Valor mínimo: <strong>R$ 10,00</strong></li>
            <li>• Horário: 24/7 (processamento em dias úteis)</li>
          </ul>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={!isValidAmount || !hasMinimumAmount || !selectedAccount}
          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuar
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}